import { Button, Heading, Text } from "@instacover/ui"
import { createFileRoute } from "@tanstack/react-router"
import { Fragment, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import { SpinnerLayout } from "@/components/layouts/SpinnerLayout"
import { useFinishStepAndNavigate } from "@/routing/useStepsRouting"
import { offlineDatabase, useStepsByType } from "@/services/dexie"

import { ExtraIcons } from "../../../../components/icons/InstructionImageType"
import { ImageLayout } from "../../../../components/layouts/ImageLayout"
import { useGeolocationRef } from "../../../../helpers/GeolocationProvider"
import { useSkipPhotoHook } from "../../../../helpers/hooks/useStore"
import { useScreenTypeIntroText } from "../../../../locales/photoType"
import { DocumentsRequiredModal } from "../../../../sections/documents/DocumentsRequiredModal"
import { useGuardedAction } from "../../../../utils/hooks/useGuardedAction"

export const Route = createFileRoute("/$sessionId/_session/documents/intro")({
  component: DocumentsIntro,
})

function useDocumentsList() {
  const { sessionId } = Route.useParams()
  const { data: documentSteps, isLoading: isLoadingSteps } = useStepsByType(sessionId, "DocumentPhoto")
  const { t } = useTranslation()

  const steps = documentSteps ?? []
  const areAllDocumentsRequired = steps.every((step) => step.minMedia > 0)
  const areAllDocumentsOptional = steps.every((step) => step.minMedia === 0)
  const documentNames: string[] = []
  const documentTypes = steps.map((step) => step.name)

  if (
    steps.find(
      (step) =>
        step.name === "VehicleRegistrationCertificateBack" || step.name === "VehicleRegistrationCertificateFront",
    )
  ) {
    documentNames.push(t("documents.type.vehicleCertificate"))
  }

  if (
    steps.find(
      (step) => step.name === "RoadworthinessCertificateBack" || step.name === "RoadworthinessCertificateFront",
    )
  ) {
    documentNames.push(t("documents.type.roadworthinessCertificate"))
  }

  if (steps.find((step) => step.name === "Invoice")) {
    documentNames.push(t("documents.type.invoice"))
  }

  if (steps.find((step) => step.name === "OtherDocument")) {
    documentNames.push(t("documents.type.otherDocument"))
  }

  if (steps.find((step) => step.name === "TransferProtocol" || step.name === "AcceptanceProtocol")) {
    documentNames.push(t("documents.type.transferProtocol"))
  }

  if (steps.find((step) => step.name === "IdentityCardFront")) {
    documentNames.push(t("documents.type.identityCardFront"))
  }

  if (steps.find((step) => step.name === "IdentityCardBack")) {
    documentNames.push(t("documents.type.identityCardBack"))
  }

  if (steps.find((step) => step.name === "AcceptanceProtocol")) {
    documentNames.push(t("documents.type.acceptanceProtocol"))
  }

  if (steps.find((step) => step.name === "DriversLicenseFront" || step.name === "DriversLicenseBack")) {
    documentNames.push(t("documents.type.driversLicense"))
  }

  if (steps.find((step) => step.name === "AccidentStatementFront")) {
    documentNames.push(t("documents.type.accidentStatement"))
  }

  return { documentNames, documentTypes, areAllDocumentsRequired, areAllDocumentsOptional, isLoadingSteps }
}

function DocumentsIntro() {
  const { sessionId } = Route.useParams()
  const { t } = useTranslation()
  const { documentNames, documentTypes, areAllDocumentsRequired, areAllDocumentsOptional, isLoadingSteps } =
    useDocumentsList()

  const { getCurrentGeolocation } = useGeolocationRef()

  const { title, text, primaryActionTitle } = useScreenTypeIntroText({ stepType: "DocumentPhoto" })

  const finishStepAndNavigate = useFinishStepAndNavigate(sessionId)
  const skipPhoto = useSkipPhotoHook()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const onContinue = useCallback(() => {
    finishStepAndNavigate("DocumentsIntro")
  }, [finishStepAndNavigate])

  async function onSkipDocuments() {
    const coords = getCurrentGeolocation()
    const location = coords
      ? { latitude: coords.latitude, longitude: coords.longitude, accuracy: coords.accuracy ?? -1 }
      : null

    // Create skipped photos for each document step + skip all document steps (besides intro)
    await Promise.all(
      documentTypes.flatMap((type) => [
        skipPhoto(type, location, sessionId),
        offlineDatabase.orderedSteps.where("[name+sessionId]").equals([type, sessionId]).modify({ isFinished: true }),
      ]),
    )

    // skip DocumentsIntro step and navigate to next route
    finishStepAndNavigate("DocumentsIntro")
  }

  // Prevents multiple clicks and disables buttons during continue or skip
  const [runContinue, isContinuing] = useGuardedAction(onContinue)
  const [runSkipDocuments, isSkipping] = useGuardedAction(onSkipDocuments)
  const isBusy = isContinuing || isSkipping

  if (isLoadingSteps) {
    return <SpinnerLayout />
  }

  return (
    <>
      <ImageLayout
        dataTestId="documents-intro"
        iconName={ExtraIcons.DocumentsIntro}
        content={
          <>
            <Heading as="h2" variant="h1">
              {title}
            </Heading>

            <div className="h-6 w-6 shrink-0" />

            <Text>{text}</Text>
          </>
        }
        footer={
          <>
            <ul className="flex flex-col gap-1">
              {documentNames.map((document) => {
                return (
                  <Fragment key={document}>
                    <li className="flex items-center justify-start gap-6">
                      <span className="bg-ic-primary size-2 rounded-full" />

                      <Text>{document}</Text>
                    </li>
                  </Fragment>
                )
              })}
            </ul>

            <div className="h-6 w-6 shrink-0" />

            <Button
              data-testid="documents-intro-continue-btn"
              type="button"
              onClick={runContinue}
              loading={isContinuing}
              disabled={isBusy}
            >
              {primaryActionTitle}
            </Button>

            {areAllDocumentsRequired && (
              <>
                <div className="h-2 w-2 shrink-0" />

                <Button
                  type="button"
                  variant="secondary"
                  disabled={isBusy}
                  onClick={() => {
                    setIsModalOpen(true)
                  }}
                >
                  {t("documents.intro.button.later")}
                </Button>
              </>
            )}

            {areAllDocumentsOptional && (
              <>
                <div className="h-2 w-2 shrink-0" />

                <Button
                  type="button"
                  variant="secondary"
                  onClick={runSkipDocuments}
                  loading={isSkipping}
                  disabled={isBusy}
                >
                  {t("documents.intro.button.skipAll")}
                </Button>
              </>
            )}

            <div className="h-11 w-11 shrink-0" />
          </>
        }
      />

      <DocumentsRequiredModal isOpen={isModalOpen} onOpenChange={setIsModalOpen} />
    </>
  )
}
